import { isCancel } from 'axios';
import {
  CrawlerOptions,
  LinkedInCrawlerContact,
  LinkedinCrawlerSearchTypes,
  RecruiterLeadPayload,
  RecruiterTalentPayload,
  RefinedRecruiterSearchPayload,
} from 'features/co-pilot/co-pilot.types';
import { ContactFilter, QualificationPromptPlaceholders } from 'features/co-pilot/campaign-builder/campaign-builder.types';
import { SearchTypes } from 'features/linkedin-crawler/linkedin-profile-finder/linkedin-profile-finder.types';
import _ from 'lodash';
import Logger from 'logger';
import { API_V1 } from './APIs/APIv1';

const getContactDataCompletenessScore = (contact: any): number => {
  if (!contact || typeof contact !== 'object') return 0;

  const currentCompanies = Array.isArray(contact.currentCompanies) ? contact.currentCompanies : [];
  const profileIds = contact.profileIds && typeof contact.profileIds === 'object' ? contact.profileIds : {};

  return [
    contact.firstName,
    contact.lastName,
    contact.fullName,
    contact.email,
    contact.location,
    contact.occupation,
    contact.headline,
    contact.linkedInURL,
    contact.assessment,
    contact.aiJudgement,
    contact.aiJudgementExplanation,
    ...currentCompanies,
    profileIds.linkedin,
    profileIds.vanity,
  ].reduce((score, value) => (typeof value === 'string' && value.trim() ? score + 1 : score), 0);
};

const normalizeContactsResponse = (data: any) => {
  if (!data) return { funnelContacts: [], total: 0, isProcessing: false };

  const normalizeJudgement = (value: unknown): string => {
    if (typeof value !== 'string') return '';
    const normalized = value.trim().toUpperCase();
    if (normalized === 'A' || normalized === 'B' || normalized === 'C') return normalized;
    if (normalized === 'ERROR') return 'Error';
    return value.trim();
  };

  const normalizeContact = (raw: any) => {
    // Legacy/new API can return either plain contact objects or campaign-contact wrappers.
    if (raw?.contactId && typeof raw.contactId === 'object') {
      const cc = raw;
      const contact = cc.contactId;
      const vanity = String(contact.vanityName || cc.profileIds?.vanity || '').trim();
      const vanityLooksPublic = vanity.length > 0 && !/^ACo/i.test(vanity) && !/^ACw/i.test(vanity) && !/^AEMAA/i.test(vanity);
      const linkedInURL =
        contact.linkedInURL ||
        contact.linkedinUrl ||
        (vanityLooksPublic ? `https://www.linkedin.com/in/${vanity.replace(/^\/+|\/+$/g, '')}/` : '');
      const currentCompany = contact.currentCompany || contact.company;
      const assessmentFromCoreAnalysis = contact.coreAnalysis?.assessment?.name;
      const assessment =
        (typeof assessmentFromCoreAnalysis === 'string' && assessmentFromCoreAnalysis.trim()) ||
        (typeof contact.assessment === 'string' && contact.assessment.trim()) ||
        (typeof cc.assessment === 'string' && cc.assessment.trim()) ||
        '';
      const aiJudgement =
        normalizeJudgement(cc.aiJudgement) ||
        normalizeJudgement(cc.judgingGrade) ||
        '';
      const aiJudgementExplanation =
        (typeof cc.aiJudgementExplanation === 'string' && cc.aiJudgementExplanation.trim()) ||
        (typeof cc.judgingExplanation === 'string' && cc.judgingExplanation.trim()) ||
        '';

      return {
        _id: cc._id,
        linkedInURL,
        stepIndex: typeof cc.stepIndex === 'number' ? cc.stepIndex : 0,
        firstName: contact.firstName || '',
        lastName: contact.lastName || '',
        fullName: contact.fullName || `${contact.firstName || ''} ${contact.lastName || ''}`.trim(),
        email: contact.email || '',
        location: contact.location || '',
        occupation: contact.occupation || contact.currentTitle || '',
        headline: contact.headline || '',
        currentCompanies: currentCompany ? [currentCompany] : [],
        profileIds: {
          linkedin: contact.linkedinProfileId || contact.linkedinId || cc.profileIds?.linkedin || '',
          vanity: contact.vanityName || cc.profileIds?.vanity || '',
        },
        isActive: !!cc.isActive,
        isFinished: !!cc.isFinished,
        isSuspended: !!cc.isSuspended,
        suspensionReason: cc.suspensionReason,
        isBlacklisted: !!contact.isBlacklisted,
        isDeleted: !!cc.isDeleted,
        // The double-contact guard writes these when it holds a candidate back. This
        // normalizer builds an explicit whitelist, so leaving them out dropped them here —
        // one layer below where 7b1c596f/40db0ad9 was assumed to fail. Two grade-A
        // candidates therefore sat inactive with no reason on screen, and it read as the
        // ranking having gone wrong rather than the guard having done its job.
        contactedElsewhere: !!cc.contactedElsewhere,
        contactedElsewhereAt: cc.contactedElsewhereAt,
        contactedElsewhereCampaignName: cc.contactedElsewhereCampaignName || '',
        contactedElsewhereReason: cc.contactedElsewhereReason || '',
        sex: contact.sex,
        assessment,
        aiJudgement,
        aiJudgementExplanation,
        isJudged: !!(cc.aiJudgement || cc.judgingGrade || cc.isJudged),
        customField: cc.customField || contact.customField || '',
        customFieldEnrichment: cc.customFieldEnrichment,
        pipelineStage: cc.pipelineStage || 'new',
        // Treat enriched / sparse / SERP-complete campaign contacts as operable
        // in the table. `preprocessedProfileAnalysis` was never on the Contact
        // schema, so enrichment's $set was stripped; CampaignContact.enrichedAt
        // and enrichmentStatus are the flags that actually persist.
        preprocessedProfileAnalysis: !!(
          contact.preprocessedProfileAnalysis ||
          contact.enrichedAt ||
          cc.enrichedAt ||
          cc.enrichmentStatus === 'completed' ||
          cc.enrichmentStatus === 'sparse'
        ),
        searchName: contact.sourceDetails?.searchTitle || contact.searchName || cc.searchName || '',
        searchId: contact.sourceDetails?.searchId || contact.searchId || cc.searchId || '',
        contactId: String(contact._id || (typeof cc.contactId === 'string' ? cc.contactId : '') || ''),
      };
    }

    const assessmentFromCoreAnalysis = raw?.coreAnalysis?.assessment?.name;
    const aiJudgement =
      normalizeJudgement(raw?.aiJudgement) ||
      normalizeJudgement(raw?.judgingGrade) ||
      '';
    const aiJudgementExplanation =
      (typeof raw?.aiJudgementExplanation === 'string' && raw.aiJudgementExplanation.trim()) ||
      (typeof raw?.judgingExplanation === 'string' && raw.judgingExplanation.trim()) ||
      '';
    return {
      ...raw,
      contactId: raw?.contactId || raw?._id || '',
      linkedInURL: raw?.linkedInURL || raw?.linkedinUrl || '',
      stepIndex: typeof raw?.stepIndex === 'number' ? raw.stepIndex : 0,
      assessment:
        (typeof raw?.assessment === 'string' && raw.assessment.trim()) ||
        (typeof assessmentFromCoreAnalysis === 'string' && assessmentFromCoreAnalysis.trim()) ||
        '',
      aiJudgement,
      aiJudgementExplanation,
      isJudged: !!(raw?.aiJudgement || raw?.judgingGrade || raw?.isJudged),
    };
  };

  const rawContacts = Array.isArray(data.funnelContacts)
    ? data.funnelContacts
    : (Array.isArray(data.campaignContacts) ? data.campaignContacts : []);

  const funnelContacts = rawContacts.map((contact: any) => normalizeContact(contact));
  const sortedFunnelContacts = _.orderBy(
    funnelContacts,
    [
      (contact) => getContactDataCompletenessScore(contact),
      (contact) => Number(Boolean(contact.preprocessedProfileAnalysis)),
      (contact) => Number(Boolean(contact.isJudged)),
    ],
    ['desc', 'desc', 'desc']
  );

  return {
    ...data,
    funnelContacts: sortedFunnelContacts,
    total: typeof data.total === 'number' ? data.total : sortedFunnelContacts.length,
    isProcessing: !!data.isProcessing,
    isProcessingProfileAnalysis: !!data.isProcessingProfileAnalysis,
  };
};

export const createSearches = async (
  funnelId: string,
  type: LinkedinCrawlerSearchTypes = LinkedinCrawlerSearchTypes.PEOPLE_SEARCH,
  urls?: string[],
  payloads?: (RecruiterTalentPayload | RecruiterLeadPayload | RefinedRecruiterSearchPayload)[],
  crawlerOptions?: CrawlerOptions,
  linkedinAccount?: string,
  searchKeywords?: LinkedInCrawlerContact[],
  searchType?: SearchTypes,
  name?: string,
  numberOfContactsToLoad?: number,
  targetJobTitles?: string[]
) => {
  try {
    const response = await API_V1.post(`/campaigns/${funnelId}/searches`, {
      type,
      urls,
      payloads,
      crawlerOptions,
      linkedinAccount,
      searchKeywords,
      searchType,
      name,
      numberOfContactsToLoad,
      targetJobTitles,
    });

    return response.data;
  } catch (e) {
    Logger.trace('Error while creating a funnel search!');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const getAllSearches = async (funnelId: string, includeFiles?: boolean, signal?: AbortSignal) => {
  try {
    const response = await API_V1.get(`/campaigns/${funnelId}/searches`, {
      params: { includeFiles },
      signal,
    });

    return response.data;
  } catch (error) {
    if (isCancel(error)) return;

    Logger.trace('Error while getting all funnel searches!');
    Logger.error(error);
    throw error;
  }
};

export const getAllFunnelContacts = async (
  funnelId: string,
  params?: { aiJudgement?: string; filter?: ContactFilter; limit?: number; offset?: number; q?: string },
  signal?: AbortSignal
) => {
  try {
    const effectiveParams =
      params?.filter === ContactFilter.IS_ACTIVE && _.isNil(params?.limit)
        ? { ...params, limit: 10000, offset: params?.offset ?? 0 }
        : params;

    const response = await API_V1.get(`/campaigns/${funnelId}/contacts`, {
      params: effectiveParams,
      signal,
    });

    return normalizeContactsResponse(response.data);
  } catch (e) {
    Logger.trace('Error while getting all funnel contacts!');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const getCampaignReceivedMessages = async (funnelId: string): Promise<any[]> => {
  try {
    const response = await API_V1.get(`/messages/campaign/${funnelId}`, {
      params: { direction: 'received', limit: 1000 },
    });
    return Array.isArray(response.data?.messages) ? response.data.messages : [];
  } catch (e) {
    Logger.error(e);
    return [];
  }
};

export const getAllFunnelContactsNew = async (
  funnelId: string,
  params?: { aiJudgement?: string; filter?: ContactFilter; limit?: number; offset?: number; q?: string },
  signal?: AbortSignal
) => {
  try {
    const effectiveParams =
      params?.filter === ContactFilter.IS_ACTIVE && _.isNil(params?.limit)
        ? { ...params, limit: 10000, offset: params?.offset ?? 0 }
        : params;

    const response = await API_V1.get(`/campaigns/${funnelId}/contacts`, {
      params: effectiveParams,
      signal,
    });

    return normalizeContactsResponse(response.data);
  } catch (error) {
    if (isCancel(error)) return;

    Logger.trace('Error while getting all funnel contacts!');
    Logger.error(error);
    throw error;
  }
};

export const updateContactIsActive = async (
  funnelId: string,
  linkedinUrls: string[],
  isActive: boolean,
  contactIds: string[] = [],
) => {
  try {
    const urls = linkedinUrls.filter((url) => Boolean(String(url || '').trim()));
    const ids = contactIds.filter((id) => Boolean(String(id || '').trim()));
    if (urls.length === 0 && ids.length === 0) {
      return { error: { message: 'No contact identifiers' }, data: [] };
    }

    const response = await API_V1.put(`/campaigns/${funnelId}/contacts`, {
      linkedinUrls: urls,
      contactIds: ids,
      isActive,
    });

    return response.data;
  } catch (e) {
    Logger.trace('Error while updating funnel contacts active status!');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const updateContactIsDeleted = async (funnelId: string, linkedinUrls: string[], isDeleted: boolean) => {
  try {
    const response = await API_V1.put(`/campaigns/${funnelId}/contacts`, {
      linkedinUrls,
      isDeleted,
    });

    return response.data;
  } catch (e) {
    Logger.trace('Error while updating funnel contacts deleted status!');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const updateContactAiJudgement = async (funnelId: string, linkedinUrls: string[], aiJudgement: string) => {
  try {
    const response = await API_V1.put(`/campaigns/${funnelId}/contacts`, {
      linkedinUrls,
      aiJudgement,
    });

    return response.data;
  } catch (e) {
    Logger.trace('Error while updating funnel contacts ai judgement!');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const updateContactIsBlacklisted = async (funnelId: string, linkedinUrls: string[], isBlacklisted: boolean) => {
  try {
    const response = await API_V1.put(`/campaigns/${funnelId}/contacts`, {
      linkedinUrls,
      isBlacklisted,
    });

    return response.data;
  } catch (e) {
    Logger.trace('Error while updating funnel contacts isBlacklisted!');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const updateContactIsSuspended = async (
  funnelId: string,
  linkedinUrls: string[],
  isSuspended: boolean,
  suspensionReason?: string
) => {
  try {
    const response = await API_V1.put(`/campaigns/${funnelId}/contacts`, {
      linkedinUrls,
      isSuspended,
      suspensionReason,
    });

    return response.data;
  } catch (e) {
    Logger.trace('Error while updating funnel contacts isBlacklisted!');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const updateSearch = async (
  funnelId: string,
  searchId: string,
  values: {
    name?: string;
    position?: string;
    url?: string;
    payload?: RefinedRecruiterSearchPayload | RecruiterTalentPayload | RecruiterTalentPayload;
    deleteContacts?: boolean;
    linkedinAccount?: string;
  }
) => {
  try {
    const response = await API_V1.patch(`/campaigns/${funnelId}/searches/${searchId}`, values);

    return response.data;
  } catch (e) {
    Logger.trace('Error while updating funnel search %s name!', searchId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const updateSearchName = async (funnelId: string, searchId: string, name: string) => {
  try {
    const response = await API_V1.patch(`/campaigns/${funnelId}/searches/${searchId}`, {
      name,
    });

    return response.data;
  } catch (e) {
    Logger.trace('Error while updating funnel search %s name!', searchId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const updateSearchLinkedinAccount = async (funnelId: string, searchId: string, linkedinAccount: string) => {
  try {
    const response = await API_V1.patch(`/campaigns/${funnelId}/searches/${searchId}`, {
      linkedinAccount,
    });

    return response.data;
  } catch (e) {
    Logger.trace('Error while updating funnel search %s linkedin account!', searchId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const deleteSearch = async (funnelId: string, searchId: string, deleteContacts?: boolean) => {
  try {
    const response = await API_V1.delete(`/campaigns/${funnelId}/searches/${searchId}`, {
      params: { deleteContacts },
    });

    return response.data ?? { success: true };
  } catch (e) {
    Logger.trace('Error while deleting funnel search %s!', searchId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const deleteFileUpload = async (funnelId: string, fileName: string) => {
  try {
    const response = await API_V1.delete(`/tasks/campaigns/${funnelId}/files`, {
      params: { fileName },
    });

    return response.data ?? { success: true };
  } catch (e) {
    Logger.trace('Error while deleting funnel file upload %s!', fileName);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const continueSearch = async (funnelId: string, searchId: string, numberOfContactsToLoad?: number) => {
  try {
    const response = await API_V1.post(`/campaigns/${funnelId}/searches/${searchId}/continue`, { numberOfContactsToLoad });

    return response.data;
  } catch (e) {
    Logger.trace('Error while trying to continue funnel search %s!', searchId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

/**
 * Re-runs a search that already finished, to pick up profiles the first pass could
 * not add. Contacts already in the campaign are skipped server-side, so this adds
 * only what is missing.
 */
export const reloadSearch = async (funnelId: string, searchId: string, numberOfContactsToLoad?: number) => {
  try {
    const response = await API_V1.post(`/campaigns/${funnelId}/searches/${searchId}/reload`, { numberOfContactsToLoad });

    return response.data;
  } catch (e) {
    Logger.trace('Error while trying to reload funnel search %s!', searchId);
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const enrichFunnelContacts = async (funnelId: string, bcApiKey: string, enrichFullFunnel: boolean) => {
  try {
    const response = await API_V1.post(`/campaigns/${funnelId}/enrich-contacts`, { bcApiKey, enrichFullCampaign: enrichFullFunnel });
    return response.data;
  } catch (e: any) {
    Logger.trace('Error while enriching funnel contacts!');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const updateFunnelContactsWithProspeos = async (funnelId: string, generateEmail: boolean, generatePhone: boolean) => {
  try {
    const response = await API_V1.post(`/campaigns/${funnelId}/update-prospeos`, { generateEmail, generatePhone });
    return response.data;
  } catch (e: any) {
    Logger.trace('Error while updating funnel contacts with Prospeos!');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const updateCustomFieldEnrichment = async (
  funnelId: string,
  personPrompt: string,
  companyPrompt: string,
  overwriteExisting: boolean
) => {
  try {
    const response = await API_V1.post(`/campaigns/${funnelId}/custom-field-enrichment`, {
      personPrompt,
      companyPrompt,
      overwriteExisting,
      runForExistingContacts: true,
    });
    return response.data;
  } catch (e: any) {
    Logger.trace('Error while updating custom field enrichment!');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const judgeFunnelProfiles = async (
  funnelId: string,
  prompt: string,
  selectedOptions: string[],
  rejudgeExistingContacts: boolean,
  isJudging: boolean = true
) => {
  try {
    const response = await API_V1.post(`/campaigns/${funnelId}/judge-profiles`, {
      prompt,
      selectedOptions,
      rejudgeExistingContacts,
      isJudging,
    });

    return response.data;
  } catch (e) {
    Logger.trace('Error while judging funnel contacts!');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const updateJudgingSettings = async (
  funnelId: string,
  data: {
    lastPrompt?: string;
    promptPlaceholders?: QualificationPromptPlaceholders;
    rejudgeExistingContacts?: boolean;
    selectedOptions?: string[];
    isJudging?: boolean;
  }
) => {
  try {
    const response = await API_V1.patch(`/campaigns/${funnelId}`, data);

    return response.data;
  } catch (e) {
    Logger.trace('Error while updating judging settings!');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const qualifyFunnelContactsRecruiting = async (
  funnelId: string,
  promptPlaceholders: QualificationPromptPlaceholders,
  selectedOptions: string[],
  rejudgeExistingContacts: boolean,
  isJudging: boolean = true
) => {
  try {
    const response = await API_V1.post(`/campaigns/${funnelId}/judge-profiles`, {
      promptPlaceholders,
      selectedOptions,
      rejudgeExistingContacts,
      isJudging,
    });

    return response.data;
  } catch (e) {
    Logger.trace('Error while judging funnel contacts!');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const getUncertainSexContacts = async (funnelId: string) => {
  try {
    const response = await API_V1.get(`/campaigns/${funnelId}/contacts/uncertain-sex`);

    return response.data;
  } catch (e) {
    Logger.trace('Error while fetching uncertain sex contacts!');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const updateContactSex = async (_funnelId: string, contactId: string, sex: 'male' | 'female') => {
  try {
    const response = await API_V1.patch(`/contacts/${contactId}`, {
      sex,
    });

    return response.data;
  } catch (e) {
    Logger.trace('Error while updating contact sex!');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

export const enrichContactsLeadList = async (funnelId: string, enrichFullFunnel: boolean = false) => {
  try {
    const body = enrichFullFunnel ? { enrichFullCampaign: true } : undefined;

    const response = await API_V1.post(`/campaigns/${funnelId}/enrich-contacts-leadlist`, body);
    return response.data;
  } catch (e: any) {
    Logger.trace('Error while enriching contacts via AI Research Agent!');
    Logger.error(e);
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};
